'use client';

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useI18n } from '@/components/i18n-provider';

type SiteBranding = {
  site_name: string;
  site_name_ar?: string | null;
  logo_url?: string | null;
  favicon_url?: string | null;
};

const DEFAULT_BRANDING: SiteBranding = {
  site_name: 'د. بشر مكي — مترجم رموز جسمك | تفسير التحاليل الطبية',
  site_name_ar: 'د. بشر مكي',
  logo_url: '',
  favicon_url: '/images/favicon.png',
};

const SiteBrandingContext = createContext<SiteBranding>(DEFAULT_BRANDING);

function ensureFavicon(url: string) {
  const id = 'dynamic-site-favicon';
  let link = document.getElementById(id) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.id = id;
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url;
}

export function SiteBrandingProvider({ children }: { children: React.ReactNode }) {
  const [branding, setBranding] = useState<SiteBranding>(DEFAULT_BRANDING);
  const { isArabic } = useI18n();

  useEffect(() => {
    const load = async () => {
      const response = await fetch('/api/site-branding');
      if (!response.ok) return;
      const data = (await response.json()) as SiteBranding;
      setBranding((prev) => ({ ...prev, ...data }));
    };
    load().catch(() => undefined);
  }, []);

  useEffect(() => {
    const favicon = branding.favicon_url?.trim();
    if (favicon) ensureFavicon(favicon);
  }, [branding]);

  return <SiteBrandingContext.Provider value={branding}>{children}</SiteBrandingContext.Provider>;
}

export function useSiteBranding() {
  const branding = useContext(SiteBrandingContext);
  const { isArabic } = useI18n();

  return useMemo(() => {
    const siteName = isArabic && branding.site_name_ar?.trim() ? branding.site_name_ar.trim() : branding.site_name;
    return {
      ...branding,
      siteName,
      logoUrl: branding.logo_url?.trim() || '',
      faviconUrl: branding.favicon_url?.trim() || '/images/favicon.png',
    };
  }, [branding, isArabic]);
}
